import { useEffect, useMemo } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { EmptyState, PrimaryButton, SecondaryButton, TextButton } from '@/lib';
import { goFleet } from '@/app/navigation';
import { orgRoute } from '@/app/org-route';
import { usePatchOnboarding, useOnboardingState } from './queries';
import { SiteStep } from './steps/Site';
import { AssetStep } from './steps/Asset';
import { SensorStep } from './steps/Sensor';
import { AssignmentStep } from './steps/Assignment';
import { InviteStep } from './steps/Invite';
import { DoneStep } from './steps/Done';

export type StepKey =
  | 'site'
  | 'asset'
  | 'sensor'
  | 'assignment'
  | 'invite'
  | 'done';

const STEP_ORDER: StepKey[] = [
  'site',
  'asset',
  'sensor',
  'assignment',
  'invite',
  'done',
];

const STEP_LABEL: Record<StepKey, string> = {
  site: 'Site',
  asset: 'Asset',
  sensor: 'Sensor',
  assignment: 'Assignment',
  invite: 'Invite teammates',
  done: 'Done',
};

const SKIPPABLE: Record<StepKey, boolean> = {
  site: false,
  asset: false,
  sensor: false,
  assignment: true,
  invite: true,
  done: false,
};

/**
 * SOnboarding wizard shell. Step routing comes from the URL search
 * param (?step=site) so a deep-link or browser-back resumes exactly
 * where the user left off. The left rail shows completion state from
 * the resource (PR-9); the body renders the active step's component.
 */
export function SOnboarding() {
  const search = useSearch({ strict: false }) as { step?: StepKey };
  const navigate = useNavigate();
  const state = useOnboardingState();
  const patch = usePatchOnboarding();

  const active: StepKey = (search.step ?? 'site') as StepKey;

  // Default-route to the first incomplete step when the user lands
  // without a ?step param — typical for clicking the AppShell banner.
  useEffect(() => {
    if (search.step) return;
    if (!state.data) return;
    if (state.data.completed_at) {
      navigate({ to: orgRoute('/onboarding'), search: { step: 'done' } });
      return;
    }
    const firstIncomplete = state.data.steps.find((s) => !s.completed);
    if (firstIncomplete) {
      navigate({
        to: orgRoute('/onboarding'),
        search: { step: firstIncomplete.key as StepKey },
      });
    }
  }, [search.step, state.data, navigate]);

  const stepIndex = useMemo(() => STEP_ORDER.indexOf(active), [active]);
  const back = stepIndex > 0 ? STEP_ORDER[stepIndex - 1] : null;
  const next = stepIndex < STEP_ORDER.length - 1 ? STEP_ORDER[stepIndex + 1] : null;

  const goTo = (s: StepKey) =>
    navigate({ to: orgRoute('/onboarding'), search: { step: s } });

  const markStepComplete = async (key: Exclude<StepKey, 'done'>) => {
    await patch.mutateAsync({
      steps: [{ key, completed: true }],
    });
  };

  if (state.isError) {
    return (
      <EmptyState
        variant="error"
        title="Couldn't load onboarding state"
        body={String(state.error)}
        cta={{ label: 'Retry', onClick: () => state.refetch() }}
      />
    );
  }

  return (
    <div className="grid gap-sp-6 lg:grid-cols-[220px_1fr]">
      <aside aria-label="Onboarding steps">
        <ol className="flex flex-col gap-sp-2">
          {STEP_ORDER.map((k) => {
            const completed =
              k === 'done'
                ? !!state.data?.completed_at
                : state.data?.steps.find((s) => s.key === k)?.completed ?? false;
            const isActive = k === active;
            return (
              <li key={k}>
                <button
                  type="button"
                  onClick={() => goTo(k)}
                  aria-current={isActive ? 'step' : undefined}
                  className={
                    'flex w-full items-center gap-sp-2 rounded-r-default px-sp-3 py-sp-2 text-left text-fs-body transition-colors ' +
                    (isActive
                      ? 'bg-gray-900 text-gray-50'
                      : 'border border-gray-300 bg-gray-50 text-gray-900 hover:border-gray-500')
                  }
                >
                  <span
                    aria-hidden
                    className={
                      'inline-flex h-sp-4 w-sp-4 items-center justify-center rounded-full text-fs-meta ' +
                      (completed
                        ? 'bg-healthy text-gray-50'
                        : 'border border-gray-500 text-gray-700')
                    }
                  >
                    {completed ? '✓' : ''}
                  </span>
                  <span>{STEP_LABEL[k]}</span>
                </button>
              </li>
            );
          })}
        </ol>
      </aside>

      <section className="flex flex-col gap-sp-4">
        <header>
          <p className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
            Step {stepIndex + 1} of {STEP_ORDER.length}
          </p>
          <h1 className="mt-sp-1 text-fs-h1 font-medium text-gray-900">
            {STEP_LABEL[active]}
          </h1>
        </header>

        <div className="rounded-r-card border border-gray-300 bg-gray-50 p-sp-6">
          {active === 'site' ? (
            <SiteStep onCompleted={() => markStepComplete('site')} />
          ) : null}
          {active === 'asset' ? (
            <AssetStep onCompleted={() => markStepComplete('asset')} />
          ) : null}
          {active === 'sensor' ? (
            <SensorStep onCompleted={() => markStepComplete('sensor')} />
          ) : null}
          {active === 'assignment' ? (
            <AssignmentStep onCompleted={() => markStepComplete('assignment')} />
          ) : null}
          {active === 'invite' ? (
            <InviteStep onCompleted={() => markStepComplete('invite')} />
          ) : null}
          {active === 'done' ? (
            <DoneStep onFinish={() => goFleet(navigate)} />
          ) : null}
        </div>

        <footer className="flex items-center justify-between gap-sp-3">
          <SecondaryButton
            label="← Back"
            onClick={() => back && goTo(back)}
            disabled={!back}
          />
          <div className="flex items-center gap-sp-3">
            {SKIPPABLE[active] && next ? (
              <TextButton label="Skip step" onClick={() => goTo(next)} />
            ) : null}
            {next ? (
              <PrimaryButton
                label="Continue →"
                onClick={() => goTo(next)}
                disabled={state.isLoading}
              />
            ) : null}
          </div>
        </footer>
      </section>
    </div>
  );
}
